import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { FolderKanban, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { ClientModel } from '@/domain/models/client.js';
import type { ProjectModel } from '@/domain/models/project.js';
import type { ListAiModels } from '@/domain/use-cases/ai-models.js';
import { DataTable, EmptyState, ErrorState, LoadingState } from '@/presentation/components/index.js';
import type { DataTableColumn } from '@/presentation/components/index.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useProjects } from '@/presentation/hooks/use-projects.js';
import { ProjectFormDialog } from '@/presentation/pages/projects/project-form-dialog.js';
import { BRAND_NAVY } from '@/shared/constants/brand.js';
import { primaryCtaGradient } from '@/shared/utils/brand-gradient.js';
import { formatDateShort } from '@/shared/utils/format-date.js';

interface Props {
    controller: ProjectController;
    clientController: ClientController;
    listAiModelsUseCase: ListAiModels;
}

export function ProjectListPage({ controller, clientController, listAiModelsUseCase }: Props) {
    const { translate } = useApp();
    const { projects, isLoading, error, search, setSearch, statusFilter, setStatusFilter, clientFilter, setClientFilter, reload } = useProjects(controller);
    const [clients, setClients] = useState<ClientModel[]>([]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [dialogProjectId, setDialogProjectId] = useState<string | undefined>(undefined);

    const openCreate = () => {
        setDialogMode('create');
        setDialogProjectId(undefined);
        setDialogOpen(true);
    };

    const openEdit = (projectId: string) => {
        setDialogMode('edit');
        setDialogProjectId(projectId);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        reload();
    };

    useEffect(() => {
        clientController
            .loadClients()
            .then((result) => {
                result.fold(
                    () => {},
                    (list) => setClients(list)
                );
            })
            .catch(() => {});
    }, [clientController]);

    const statusOptions: Array<{ value: string; label: string }> = [
        { value: 'ATIVO', label: translate('projects.active') },
        { value: 'INATIVO', label: translate('projects.inactive') },
        { value: '', label: translate('projects.all') }
    ];

    const gradientBtn = {
        textTransform: 'none' as const,
        background: primaryCtaGradient,
        color: 'white',
        '&:hover': { opacity: 0.9, background: primaryCtaGradient }
    };

    const columns: DataTableColumn<ProjectModel>[] = useMemo(
        () => [
            {
                key: 'name',
                header: translate('projects.name'),
                sortValue: (project) => project.name,
                render: (project) => (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: `${BRAND_NAVY}1A`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FolderKanban size={14} style={{ color: BRAND_NAVY }} />
                        </Box>
                        <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={500}>
                                {project.name}
                            </Typography>
                            <Chip
                                label={project.abbreviation}
                                size="small"
                                sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', color: 'text.secondary', fontSize: 11, height: 18 }}
                            />
                        </Stack>
                    </Stack>
                )
            },
            {
                key: 'clientName',
                header: translate('projects.client'),
                sortValue: (project) => project.clientName,
                render: (project) => (
                    <Typography variant="body2" color="text.secondary">
                        {project.clientName}
                    </Typography>
                )
            },
            {
                key: 'status',
                header: translate('projects.status'),
                sortValue: (project) => project.status,
                render: (project) => (
                    <Chip
                        label={translate(project.status === 'ATIVO' ? 'projectStatusLabels.ATIVO' : 'projectStatusLabels.INATIVO')}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            bgcolor: project.status === 'ATIVO' ? '#DCFCE7' : 'action.selected',
                            color: project.status === 'ATIVO' ? '#166534' : 'text.secondary'
                        }}
                    />
                )
            },
            {
                key: 'developmentsCount',
                header: translate('projects.devs'),
                sortValue: (project) => project.developmentsCount,
                render: (project) => (
                    <Typography variant="body2" color="text.secondary">
                        {project.developmentsCount}
                    </Typography>
                )
            },
            {
                key: 'createdAt',
                header: translate('projects.createdAtLabel'),
                sortValue: (project) => project.createdAt,
                render: (project) => (
                    <Typography variant="body2" color="text.secondary">
                        {formatDateShort(project.createdAt)}
                    </Typography>
                )
            }
        ],
        [translate]
    );

    if (isLoading && projects.length === 0) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                        {translate('projects.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {translate('projects.manageProjects')}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate} disableElevation sx={gradientBtn}>
                    {translate('projects.new')}
                </Button>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                <TextField
                    placeholder={translate('projects.searchByNameOrAbbr')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 220 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={16} />
                            </InputAdornment>
                        )
                    }}
                />
                <TextField select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} size="small" SelectProps={{ displayEmpty: true }} sx={{ minWidth: 200 }}>
                    <MenuItem value="">{translate('projects.allClients')}</MenuItem>
                    {clients.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                            {c.name}
                        </MenuItem>
                    ))}
                </TextField>
                <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    {statusOptions.map((opt) => (
                        <Button
                            key={opt.value}
                            size="small"
                            onClick={() => setStatusFilter(opt.value)}
                            disableElevation
                            sx={{
                                textTransform: 'none',
                                minWidth: 64,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 0,
                                boxShadow: 'none',
                                bgcolor: statusFilter === opt.value ? BRAND_NAVY : 'background.paper',
                                color: statusFilter === opt.value ? 'white' : 'text.secondary',
                                '&:hover': { bgcolor: statusFilter === opt.value ? BRAND_NAVY : 'action.hover', boxShadow: 'none' }
                            }}
                        >
                            {opt.label}
                        </Button>
                    ))}
                </Box>
            </Stack>

            {projects.length === 0 ? (
                <EmptyState
                    icon={<FolderKanban size={24} />}
                    title={translate('projects.noProjectsForClient')}
                    message={translate('projects.startByRegisteringFirstProject')}
                    action={
                        <Button variant="contained" onClick={openCreate} disableElevation sx={gradientBtn}>
                            {translate('projects.registerFirstProject')}
                        </Button>
                    }
                />
            ) : (
                <DataTable columns={columns} rows={projects} getRowKey={(project) => project.id} onRowClick={(project) => openEdit(project.id)} />
            )}

            <ProjectFormDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={handleSaved}
                controller={controller}
                clientController={clientController}
                listAiModelsUseCase={listAiModelsUseCase}
                mode={dialogMode}
                projectId={dialogProjectId}
            />
        </Box>
    );
}
